import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Создаем контекст для режима редактирования
const EditModeContext = createContext({
  editMode: false,
  setEditMode: () => {},
  selectedElement: null,
  setSelectedElement: () => {},
  editableProperties: {},
  updateElementProperty: () => {},
  saveChanges: () => {},
  backupStatus: null,
  createBackup: () => {},
});

export const useEditMode = () => useContext(EditModeContext);

export const EditModeProvider = ({ children }) => {
  const [editMode, setEditMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [editableProperties, setEditableProperties] = useState({});
  const [changedElements, setChangedElements] = useState({});
  const [backupStatus, setBackupStatus] = useState(null); // статус последнего резервного копирования
  const [isBackupCreating, setIsBackupCreating] = useState(false);

  // Функция для создания резервной копии проекта
  const createBackup = async () => {
    try {
      setIsBackupCreating(true);
      setBackupStatus({ status: 'creating', message: 'Создание резервной копии...' });
      
      // В реальном приложении это будет вызов API бэкенда
      console.log('Резервная копия создана перед началом редактирования');
      setBackupStatus({ status: 'success', message: 'Резервная копия успешно создана', timestamp: new Date().toISOString() });
      return true;
    } catch (error) {
      console.error('Ошибка при создании резервной копии:', error);
      setBackupStatus({ status: 'error', message: 'Ошибка при создании резервной копии', error: error.message });
      return false;
    } finally {
      setIsBackupCreating(false);
    }
  };

  // При загрузке проверяем URL на наличие параметра editMode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('editMode') === 'true') {
      // Сразу активируем режим редактирования
      setEditMode(true);
      // Создаем резервную копию
      createBackup();
    }
  }, []);

  // Функция обновления свойства элемента
  const updateElementProperty = (elementId, property, value) => {
    setEditableProperties(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [property]: value,
      }
    }));

    // Сохраняем изменение в список измененных элементов
    setChangedElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [property]: value,
      }
    }));
  };

  // Функция сохранения изменений на сервере
  const saveChanges = async () => {
    try {
      // Здесь должен быть API-запрос для сохранения изменений
      console.log('Сохраняем изменения:', changedElements);
      
      // Очищаем список измененных элементов после успешного сохранения
      setChangedElements({});
      
      return { success: true, message: 'Изменения успешно сохранены' };
    } catch (error) {
      console.error('Ошибка при сохранении изменений:', error);
      return { success: false, message: 'Ошибка при сохранении изменений' };
    }
  };

  const contextValue = {
    editMode,
    setEditMode,
    selectedElement,
    setSelectedElement,
    editableProperties,
    updateElementProperty,
    saveChanges,
    backupStatus,
    createBackup,
    isBackupCreating,
  };

  return (
    <EditModeContext.Provider value={contextValue}>
      {children}
    </EditModeContext.Provider>
  );
};

export default EditModeContext;
